export interface Project {
  id: string;
  slug: string;
  title: string;
  category: string;
  tags: string[];
  year: string;
  client: string;
  description: string;
  longDescription: string;
  challenge: string;
  solution: string;
  result: string;
  gradient: string;
  letter: string;
  images: string[];
  featured: boolean;
  link?: string;
}

export const projects: Project[] = [
  // ── App Development ──
  {
    id: '1',
    slug: 'wallet-app',
    title: 'Wallet - Secure Offline Wallet',
    category: 'Mobile Apps',
    tags: ['Mobile', 'Android', 'Finance', 'Security'],
    year: '2024',
    client: 'Personal Project',
    description: 'The ultimate secure offline wallet for managing your finances with complete privacy.',
    longDescription: 'Wallet is a privacy-first financial app that lets you manage your finances without any cloud dependency. All data stay on-device, ensuring maximum security. The app features a clean, intuitive interface with support for multiple accounts, transaction tracking, and budget insights.',
    challenge: 'Build a financial app that prioritizes security and offline functionality without sacrificing usability.',
    solution: 'Implemented local-first architecture with encrypted storage, clean UI patterns, and intuitive transaction management — all without requiring internet connectivity.',
    result: 'Published on Google Play Store with strong user ratings for privacy and ease of use.',
    gradient: 'linear-gradient(135deg, #1ed760 0%, #0d7a3a 100%)',
    letter: 'W',
    images: ['/images/AppDevelopment/Wallet.png'],
    featured: true,
    link: 'https://play.google.com/store/apps/details?id=com.sidhant.wallet',
  },
  {
    id: '2',
    slug: 'nuzzle-puzzle',
    title: 'Nuzzle - Endless Image Puzzles',
    category: 'Mobile Apps',
    tags: ['Mobile', 'Android', 'Game', 'Puzzle'],
    year: '2024',
    client: 'Personal Project',
    description: 'An image puzzle game with endless puzzles generated from beautiful Pexels photography.',
    longDescription: 'Nuzzle transforms stunning photography from Pexels into engaging sliding puzzles. With thousands of images to choose from and multiple difficulty levels, the app provides endless entertainment while showcasing beautiful photography.',
    challenge: 'Create an engaging puzzle game that integrates with external image APIs and provides smooth gameplay.',
    solution: 'Built a performant game engine with image caching, multiple difficulty levels, and a beautiful gallery of completed puzzles.',
    result: 'Successfully published on Google Play with a growing user base.',
    gradient: 'linear-gradient(135deg, #539df5 0%, #2858a8 100%)',
    letter: 'N',
    images: ['/images/AppDevelopment/Nuzzle.png'],
    featured: true,
    link: 'https://play.google.com/store/apps/details?id=com.sidhant.nuzzle',
  },
  {
    id: '3',
    slug: 'calculator-app',
    title: 'Calculator - Simple & Sleek',
    category: 'Mobile Apps',
    tags: ['Mobile', 'Android', 'Utility', 'Calculator'],
    year: '2024',
    client: 'Personal Project',
    description: 'A beautifully designed calculator with a simple and sleek interface for everyday calculations.',
    longDescription: 'Calculator combines minimal design with powerful functionality. The clean interface makes everyday calculations fast and intuitive, while the thoughtful design ensures every interaction feels responsive and polished.',
    challenge: 'Stand out in a saturated calculator app market with superior design and user experience.',
    solution: 'Focused on absolute minimalism — a clutter-free interface, smooth animations, and haptic feedback for a satisfying calculation experience.',
    result: 'Published on Google Play Store, praised for its clean design and fast performance.',
    gradient: 'linear-gradient(135deg, #ffa42b 0%, #a8650d 100%)',
    letter: 'C',
    images: ['/images/AppDevelopment/Calculator.png'],
    featured: true,
    link: 'https://play.google.com/store/apps/details?id=com.sidhant.calculator',
  },
  {
    id: '4',
    slug: 'path-step-counter',
    title: 'Path - Privacy-First Step Counter',
    category: 'Mobile Apps',
    tags: ['Mobile', 'Android', 'Health', 'Privacy'],
    year: '2024',
    client: 'Personal Project',
    description: 'A step counter app built on the principle of privacy — track your steps without sharing your data.',
    longDescription: 'Path is a health and fitness app that respects your privacy. No accounts, no cloud sync, no tracking. Just accurate step counting using your device\'s sensors. The app provides daily, weekly, and monthly statistics with a beautiful, minimal interface.',
    challenge: 'Build an accurate step counter that works entirely offline while providing meaningful health insights.',
    solution: 'Leveraged native sensor APIs for accurate tracking, local storage for history, and clean data visualization for progress tracking.',
    result: 'Published on Google Play with positive reception for privacy-focused approach.',
    gradient: 'linear-gradient(135deg, #a855f7 0%, #6b21a8 100%)',
    letter: 'P',
    images: ['/images/AppDevelopment/Path.png'],
    featured: true,
    link: 'https://play.google.com/store/apps/details?id=com.sidhant.path',
  },

  // ── Web Design ──
  {
    id: '5',
    slug: 'beauty-portfolio',
    title: 'Beauty — Creative Portfolio',
    category: 'Web Design',
    tags: ['Web', 'Portfolio', 'Responsive', 'Animation'],
    year: '2025',
    client: 'Creative Agency',
    description: 'A stunning animated portfolio website with smooth transitions and immersive visual storytelling.',
    longDescription: 'A high-performance portfolio website designed for a creative agency. Features include smooth page transitions, scroll-triggered animations, responsive design, and optimized image loading. Built with modern web technologies for maximum performance.',
    challenge: 'Create a visually impressive website that loads fast and works beautifully on all devices.',
    solution: 'Used progressive image loading, CSS animations, and code splitting for a fast, beautiful experience.',
    result: '95+ Lighthouse score across all metrics, with significant increase in engagement time.',
    gradient: 'linear-gradient(135deg, #84cc16 0%, #4d7c0f 100%)',
    letter: 'B',
    images: ['/images/WebDesign/Beauty.png'],
    featured: true,
  },
  {
    id: '6',
    slug: 'cocacola-ecommerce',
    title: 'CocaCola — E-commerce Platform',
    category: 'Web Design',
    tags: ['Web', 'E-commerce', 'Shopping', 'Responsive'],
    year: '2024',
    client: 'Retail Brand',
    description: 'A modern e-commerce platform with seamless checkout, product browsing, and inventory management.',
    longDescription: 'Full-featured e-commerce website built for a growing retail brand. Includes product catalog, search and filtering, cart management, secure checkout, and order tracking. Optimized for conversions with clean UX patterns.',
    challenge: 'Build a scalable e-commerce platform that handles complex product variants while maintaining a simple shopping experience.',
    solution: 'Modular architecture with reusable product components, optimized checkout flow, and integrated payment processing.',
    result: '40% increase in conversion rate compared to previous platform.',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #991b1b 100%)',
    letter: 'C',
    images: ['/images/WebDesign/CocaCola.png'],
    featured: false,
  },
  {
    id: '7',
    slug: 'jewellary-luxury-store',
    title: 'Jewellary — Luxury Brand Website',
    category: 'Web Design',
    tags: ['Web', 'Luxury', 'Jewelry', 'Responsive'],
    year: '2024',
    client: 'Luxury Brand',
    description: 'An elegant, high-end website for a premium jewelry brand with immersive product showcases.',
    longDescription: 'A luxury e-commerce and brand experience website for a premium jewelry label. Features include high-resolution product galleries, subtle micro-interactions, and a refined editorial layout that lets each piece speak for itself.',
    challenge: 'Convey luxury craftsmanship and premium quality through a digital experience.',
    solution: 'Minimalist layouts, generous white space, and carefully choreographed image reveals create a gallery-like browsing experience.',
    result: 'Increased average session duration by 60% and elevated brand perception.',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
    letter: 'J',
    images: ['/images/WebDesign/Jewellary.png'],
    featured: true,
  },
  {
    id: '8',
    slug: 'wildlife-conservation',
    title: 'Wildlife — Conservation Platform',
    category: 'Web Design',
    tags: ['Web', 'Conservation', 'Nonprofit', 'Responsive'],
    year: '2024',
    client: 'Conservation Org',
    description: 'A nature-inspired website for a wildlife conservation organization with storytelling-driven layouts.',
    longDescription: 'A storytelling-first website for a wildlife conservation organization. The design uses rich photography, scroll-driven narratives, and impactful data visualization to engage visitors and drive donations for conservation efforts.',
    challenge: 'Balance emotional storytelling with clear calls-to-action and donation flows.',
    solution: 'Created an immersive narrative experience with scroll-triggered reveals, paired with a streamlined donation funnel.',
    result: '35% increase in online donations and significantly higher engagement on campaign pages.',
    gradient: 'linear-gradient(135deg, #22c55e 0%, #15803d 100%)',
    letter: 'W',
    images: ['/images/WebDesign/Wildlife.png'],
    featured: false,
  },
  {
    id: '9',
    slug: 'zendesk-help-center',
    title: 'ZenDesk — Help Center Redesign',
    category: 'Web Design',
    tags: ['Web', 'SaaS', 'Help Center', 'UX'],
    year: '2025',
    client: 'SaaS Company',
    description: 'A clean, user-friendly help center redesign focused on fast issue resolution and self-service.',
    longDescription: 'A complete redesign of a SaaS help center platform focused on reducing support ticket volume. The new design features a prominent search experience, organized article categories, contextual suggestions, and a clean reading experience for technical documentation.',
    challenge: 'Help users find answers quickly within a massive knowledge base without overwhelming them.',
    solution: 'Intelligent search with autocomplete, clearly structured categories, and related article recommendations reduced time-to-answer.',
    result: '25% reduction in support tickets and improved user satisfaction scores.',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0e7490 100%)',
    letter: 'Z',
    images: ['/images/WebDesign/ZenDesk.png'],
    featured: true,
  },
  {
    id: '10',
    slug: 'custom-pc-configurator',
    title: 'Custom PC — Build Configurator',
    category: 'Web Design',
    tags: ['Web', 'E-commerce', 'Configurator', 'Tech'],
    year: '2025',
    client: 'PC Builder Brand',
    description: 'An interactive PC build configurator that lets users customize components with real-time compatibility checks and pricing.',
    longDescription: 'A full-featured PC build configurator website that transforms the complex process of building a custom computer into an intuitive, visual experience. Users select from compatible CPUs, GPUs, RAM, storage, and cases with real-time power calculations, bottleneck warnings, and price updates. The interface combines technical depth with approachable design.',
    challenge: 'Present hundreds of PC components in a way that guides beginners while satisfying expert builders.',
    solution: 'Built a smart filtering system that auto-filters compatible parts, a visual build preview that updates in real-time, and a pricing engine that compares across retailers.',
    result: 'Users complete builds 40% faster than traditional text-based configurators, with higher conversion rates.',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)',
    letter: 'P',
    images: ['/images/WebDesign/CustomPC.png'],
    featured: true,
  },

  // ── Reddit Design ──
  {
    id: '11',
    slug: 'reddit-90s-hiphop',
    title: 'r/90sHipHop — Subreddit Redesign',
    category: 'Reddit Design',
    tags: ['Reddit', 'Banner', 'Logo', 'Mobile'],
    year: '2024',
    client: 'Reddit Community',
    description: 'Complete subreddit banner and logo redesign for r/90sHipHop with retro hip-hop aesthetics.',
    longDescription: 'A full visual identity redesign for the r/90sHipHop subreddit. The design captures the golden era of hip-hop with vibrant retro aesthetics, spanning desktop banners, mobile banners, and a custom subreddit logo — all cohesive and on-brand for the community.',
    challenge: 'Create a visually striking subreddit identity that captures 90s hip-hop culture across multiple device formats.',
    solution: 'Designed a unified visual system with consistent color palettes, typography, and imagery that scales from desktop-wide banners down to compact mobile and logo formats.',
    result: 'Delivered a polished, community-ready subreddit identity with desktop, mobile, and logo assets.',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
    letter: '9',
    images: [
      '/images/Reddit/90sHipHop/logo.png',
      '/images/Reddit/90sHipHop/desktop.png',
      '/images/Reddit/90sHipHop/mobile.png',
    ],
    featured: true,
  },
  {
    id: '12',
    slug: 'reddit-clever-comeback',
    title: 'r/CleverComeback — Subreddit Redesign',
    category: 'Reddit Design',
    tags: ['Reddit', 'Banner', 'Logo', 'Mobile'],
    year: '2024',
    client: 'Reddit Community',
    description: 'A multi-variant subreddit redesign for r/CleverComeback with 5 logo options, desktop banner, and 3 mobile banners.',
    longDescription: 'An extensive subreddit design project for r/CleverComeback featuring multiple design variations. Delivered 5 unique logo concepts, a desktop banner, and 3 distinct mobile banner variants — giving the community options to choose the best fit for their brand.',
    challenge: 'Provide enough variety in design options while maintaining a cohesive identity across all assets.',
    solution: 'Created a modular design system with a core visual theme, then iterated on logo variations and mobile banner alternatives to give the community flexibility.',
    result: 'Delivered 9 design assets across logo, desktop, and mobile formats for a complete subreddit toolkit.',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #5b21b6 100%)',
    letter: 'C',
    images: [
      '/images/Reddit/CleverComeback/logo_1.png',
      '/images/Reddit/CleverComeback/logo_2.png',
      '/images/Reddit/CleverComeback/logo_3.png',
      '/images/Reddit/CleverComeback/logo_4.png',
      '/images/Reddit/CleverComeback/logo_5.png',
      '/images/Reddit/CleverComeback/desktop.png',
      '/images/Reddit/CleverComeback/mobile_1.png',
      '/images/Reddit/CleverComeback/mobile_2.png',
      '/images/Reddit/CleverComeback/mobile_3.png',
    ],
    featured: true,
  },
  {
    id: '13',
    slug: 'reddit-youtube-india',
    title: 'r/youtubeindia — Subreddit Redesign',
    category: 'Reddit Design',
    tags: ['Reddit', 'Banner', 'Logo', 'Mobile'],
    year: '2024',
    client: 'Reddit Community',
    description: 'Subreddit banner and logo redesign for r/youtubeindia, India\'s YouTube creator community on Reddit.',
    longDescription: 'A subreddit redesign for r/youtubeindia — a community for Indian YouTube creators and enthusiasts. The design includes a custom logo, desktop banner, and mobile-optimized banner, all reflecting the energy and diversity of India\'s creator economy.',
    challenge: 'Design an identity that represents a diverse, national-level creator community while working across all subreddit display sizes.',
    solution: 'Built a bold, recognizable logo paired with complementary desktop and mobile banners that maintain visual consistency at every scale.',
    result: 'A clean, professional subreddit identity delivered across all required formats.',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #991b1b 100%)',
    letter: 'Y',
    images: [
      '/images/Reddit/youtubeindia/logo.png',
      '/images/Reddit/youtubeindia/desktop.png',
      '/images/Reddit/youtubeindia/mobile.png',
    ],
    featured: true,
  },
  {
    id: '14',
    slug: 'reddit-desktop-banners-collection',
    title: 'Desktop Banners Collection',
    category: 'Reddit Design',
    tags: ['Reddit', 'Desktop', 'Banner', 'Collection'],
    year: '2024',
    client: 'Various',
    description: 'A curated collection of 7 desktop subreddit banners showcasing diverse design styles and themes.',
    longDescription: 'A collection of 7 desktop banner designs created for various subreddit redesign projects. Each banner is crafted at desktop resolution with attention to composition, color, typography, and visual storytelling — spanning genres from music and gaming to community and culture.',
    challenge: 'Curate a showcase of desktop banners that demonstrates range and versatility in subreddit design.',
    solution: 'Selected 7 standout banners from various projects, each representing a different visual approach and community theme.',
    result: 'A diverse portfolio of desktop banner work ready for presentation and review.',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0e7490 100%)',
    letter: 'D',
    images: [
      '/images/Reddit/Desktop_Banners_extra/desktop_1.png',
      '/images/Reddit/Desktop_Banners_extra/desktop_2.png',
      '/images/Reddit/Desktop_Banners_extra/desktop_3.png',
      '/images/Reddit/Desktop_Banners_extra/desktop_4.png',
      '/images/Reddit/Desktop_Banners_extra/desktop_5.png',
      '/images/Reddit/Desktop_Banners_extra/desktop_6.png',
      '/images/Reddit/Desktop_Banners_extra/desktop_7.png',
    ],
    featured: true,
  },
  {
    id: '15',
    slug: 'reddit-logo-collection',
    title: 'Logo Design Collection',
    category: 'Reddit Design',
    tags: ['Reddit', 'Logo', 'Branding', 'Collection'],
    year: '2024',
    client: 'Various',
    description: 'A collection of 13 subreddit logo designs spanning a wide range of themes and visual styles.',
    longDescription: 'A showcase of 13 subreddit logo designs, each crafted to be distinctive and memorable. These logos cover a broad spectrum of themes — from minimalist icons to detailed illustrative marks — demonstrating versatility in subreddit brand identity design.',
    challenge: 'Present a cohesive collection of logos that each stands on its own while showing the breadth of subreddit design capabilities.',
    solution: 'Curated 13 of the strongest logo designs, each tailored to its community\'s unique personality and theme.',
    result: '13 unique subreddit logos showcasing range in style, color, and concept.',
    gradient: 'linear-gradient(135deg, #ec4899 0%, #9d174d 100%)',
    letter: 'L',
    images: [
      '/images/Reddit/Logo_extra/logo_1.png',
      '/images/Reddit/Logo_extra/logo_2.png',
      '/images/Reddit/Logo_extra/logo_3.png',
      '/images/Reddit/Logo_extra/logo_4.png',
      '/images/Reddit/Logo_extra/logo_5.png',
      '/images/Reddit/Logo_extra/logo_6.png',
      '/images/Reddit/Logo_extra/logo_7.png',
      '/images/Reddit/Logo_extra/logo_8.png',
      '/images/Reddit/Logo_extra/logo_9.png',
      '/images/Reddit/Logo_extra/logo_10.png',
      '/images/Reddit/Logo_extra/logo_11.png',
      '/images/Reddit/Logo_extra/logo_12.png',
      '/images/Reddit/Logo_extra/logo_13.png',
    ],
    featured: true,
  },
  {
    id: '16',
    slug: 'reddit-mobile-banners-collection',
    title: 'Mobile Banners Collection',
    category: 'Reddit Design',
    tags: ['Reddit', 'Mobile', 'Banner', 'Collection'],
    year: '2024',
    client: 'Various',
    description: 'A collection of 7 mobile-optimized subreddit banners designed for compact screen formats.',
    longDescription: 'A curated set of 7 mobile banner designs created for subreddit redesigns. Each banner is optimized for mobile viewport dimensions while maintaining visual impact and readability on smaller screens. Mobile banners require tighter composition and stronger focal points — these designs deliver.',
    challenge: 'Design banners that remain impactful and legible on the constrained mobile viewport.',
    solution: 'Focused compositions with strong central elements, bold typography, and high-contrast color palettes for mobile readability.',
    result: '7 mobile-optimized banners that demonstrate responsive design thinking for subreddit identities.',
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)',
    letter: 'M',
    images: [
      '/images/Reddit/Mobile_Banner_extra/mobile_1.png',
      '/images/Reddit/Mobile_Banner_extra/mobile_2.png',
      '/images/Reddit/Mobile_Banner_extra/mobile_3.png',
      '/images/Reddit/Mobile_Banner_extra/mobile_4.png',
      '/images/Reddit/Mobile_Banner_extra/mobile_5.png',
      '/images/Reddit/Mobile_Banner_extra/mobile_6.png',
      '/images/Reddit/Mobile_Banner_extra/mobile_7.png',
    ],
    featured: false,
  },
];

export const categories = ['All', 'Mobile Apps', 'Web Design', 'Reddit Design'];

export function getProjectBySlug(slug: string): Project | undefined {
  return projects.find(p => p.slug === slug);
}

export function getFeaturedProjects(): Project[] {
  return projects.filter(p => p.featured);
}

export function getProjectsByCategory(category: string): Project[] {
  if (category === 'All') return projects;
  return projects.filter(p => p.category === category);
}
